import React, { useEffect, useRef, useState } from "react";
import "./ReviewSection.css";

const ALL_REVIEWS_URL =
  "https://www.google.com/search?q=cosmetink#lrd=0x89c443557b11e64d:0x601c04db1170f5b6,1,,,";
/* ^ keep your existing lrd deeplink; Google rewrites as needed */

function Stars({ count = 5 }) {
  return (
    <div className="rev-stars" aria-label={`${count} out of 5 stars`}>
      {"★★★★★".slice(0, count)}
    </div>
  );
}

function ReviewCard({ name, text, stars = 5 }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <article className="rev-card af-observe">
      <header className="rev-head">
        <div className="rev-name">{name}</div>
        <Stars count={stars} />
      </header>

      <p className={`rev-text ${expanded ? "is-open" : ""}`}>
        {text}
      </p>

      {/* Show expander only for longer blocks (CSS clamps lines) */}
      <button
        type="button"
        className="rev-more"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {expanded ? "Show less" : "Read more"}
      </button>
    </article>
  );
}

function SnapRow({ children, ariaLabel }) {
  const scrollerRef = useRef(null);
  const [showHint, setShowHint] = useState(true);
  const isTouch =
    typeof window !== "undefined" &&
    matchMedia("(pointer: coarse)").matches;

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const id = window.requestAnimationFrame(() => {
      el.scrollTo({ left: 0, behavior: "auto" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [children]);

  useEffect(() => {
    const t = setTimeout(() => setShowHint(false), 2200);
    return () => clearTimeout(t);
  }, []);

  const scrollByAmount = (dir = 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector(".rev-card");
    const step = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <div className="rev-strip">
      <button
        className="rev-nav rev-nav--prev"
        aria-label="Scroll previous"
        onClick={() => scrollByAmount(-1)}
      >
        ‹
      </button>

      <div
        ref={scrollerRef}
        className="rev-strip-scroll"
        role="group"
        aria-label={ariaLabel}
        onWheel={() => setShowHint(false)}
        onPointerDown={() => setShowHint(false)}
      >
        {children}
      </div>

      <button
        className="rev-nav rev-nav--next"
        aria-label="Scroll next"
        onClick={() => scrollByAmount(1)}
      >
        ›
      </button>

      {/* edge fades */}
      <div className="rev-edge rev-edge--left" aria-hidden="true" />
      <div className="rev-edge rev-edge--right" aria-hidden="true" />

      {showHint && (
        <div className="rev-hint" aria-hidden="true">
          {isTouch ? "Swipe to browse" : "Drag to scroll"}
        </div>
      )}
    </div>
  );
}

export default function ReviewsSection() {
  const rootRef = useRef(null);

  // reveal-on-scroll (scoped)
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = Array.from(root.querySelectorAll(".af-observe"));
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Your data (trimmed to essentials; add/remove freely)
  const reviews = [
    {
      name: "Antoinette Hernandez",
      text:
        "I can't express enough how absolutely incredible my experience at Cosmetink has been! About a year and a half ago, I embarked on this exciting journey with Nikky's microblading course... If you're contemplating your training destination in the makeup industry, without a doubt, I wholeheartedly recommend Cosmetink.",
    },
    {
      name: "Taylor Anthony",
      text:
        "Nikki and Sean are the sweetest humans alive! So kind, so thorough and so passionate about what they teach! I’ll always go back for new training. The techniques are precise and the student work is stunning. Absolutely the place to learn PMU!",
    },
    {
      name: "Jennifer V.",
      text:
        "Classes were detailed, relevant, and the instructors made learning seamless. They’re knowledgeable, patient, and kind—exactly what I need to learn well. The environment and demeanor made the process worth it every step of the way.",
    },
    {
      name: "Amber McQuay",
      text:
        "This is my 3rd certification through Cosmetink. Lip blush was phenomenal. I took the apprenticeship too, then ombré brows. The master instructors are extremely talented—an honor to learn their artistry and techniques.",
    },
    {
      name: "Melody Blossom",
      text:
        "Amazing school experience! Positive, encouraging, supportive—and great teachers. They ensured I understood each step and provided consistent feedback. I’m certified in lip blush and ombré brows and can’t wait for my apprenticeship!",
    },
    {
      name: "Lien Pham",
      text:
        "Beyond perfect. I was down with my lashing business and their classes brought me back to life. Meeting other artists and learning together was inspiring. The best student experience I’ve had—technique + confidence!",
    },
    {
      name: "Amber H",
      text:
        "Took the lip blush certification and it went far beyond expectations. Teaching was personable, kind, and genuinely invested in my learning. I left set up for success and already miss class!",
    },
    {
      name: "Taylor Meli",
      text:
        "Loved taking microblading with Nikky and Sean! They made me feel comfortable despite my anxiety. After my first model I wanted to do more. They continue to support me—100% recommend!",
    },
    {
      name: "Anna Martinez",
      text:
        "I took the ombré powder brows course with zero experience. Sean and Nikky are amazing teachers—clear, patient, and confidence-building. Worth it 1000%.",
    },
    {
      name: "Alicia Mecca",
      text:
        "Two-day ombré brow course. These educators are professionals—clear, honest guidance with top-tier materials. I left with confidence and knowledge. SO worth the money.",
    },
    {
      name: "Keiera Kisel",
      text:
        "Waited months for the ombré class—worth it. Super knowledgeable, talented, and they want students to succeed. I’ll be back for more classes.",
    },
    {
      name: "julia r",
      text:
        "Beginner’s ombré brow course. Beautiful studio, amazing trainers. Incredible value—top-of-the-line machine and pigments included. Left feeling confident with real next steps.",
    },
    {
      name: "Lindsey Ruch",
      text:
        "Ombré brows class was immersive and fun. Instructors were helpful and positive. You never leave unsure. Absolutely 5 stars—I’ll be back for more.",
    },
    {
      name: "corrin bishop",
      text:
        "Ombré powder brow course was the perfect intro. Three instructors with strong techniques, plenty of demo and practice, and a super positive environment.",
    },
    {
      name: "Hogla Jimenez",
      text:
        "Best experience as a student—certified in Ombré Powder Brows with the best academy. So proud!",
    },
  ];

  return (
    <section className="rev-section" ref={rootRef} aria-label="Reviews from our graduates">
      <div className="rev-headline c af-observe">
        <h2 className="rev-title">
          <span className="rev-grad">Reviews</span>
          <br />
          From Our Graduates
        </h2>

        <a className="rev-all" href={ALL_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
          See all reviews
          <span className="rev-ext" aria-hidden="true">↗</span>
        </a>
      </div>

      <SnapRow ariaLabel="Scrollable student reviews">
        {reviews.map((r, i) => (
          <ReviewCard key={i} name={r.name} text={r.text} stars={5} />
        ))}
      </SnapRow>
    </section>
  );
}
